// ゴミ箱のキャンバスを完全に削除する前の確認ダイアログ。
//
// キャンバス名と「元に戻せない」ことを示し、確認の文字列（「完全に削除」）の手入力が完全に一致したときだけ
// 削除ボタンを有効にする。前後の空白の除去・部分一致・placeholder の代用はしない。貼り付け・ドラッグ＆ドロップは
// 止め、自動入力（オートフィル）などの手入力でない値では有効にしない。日本語入力の変換中は無効にし、変換確定の
// Enter を削除の送信に使わない（フォームを置かず、Enter では何も送らない）。閉じるのはキャンセルと Esc だけ
// （日本語入力の変換中の Esc は変換の取り消しとして扱い、閉じない）。
// この確認は誤操作を防ぐためのもので、認可の境界ではない（サーバが権限・状態・世代・確認文字列を独立して検証する）。

import { Button, Modal, OutlinedInputText, type useThemeMode } from "@meiden-connect/ui";
import {
  CANVAS_PERMANENT_DELETE_CONFIRMATION_TEXT,
  isCanvasPermanentDeleteConfirmed,
} from "@refinement-canvas/domain";
import {
  useEffect,
  useRef,
  useState,
  type ClipboardEvent as ReactClipboardEvent,
  type DragEvent as ReactDragEvent,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { DASHBOARD_THEME_MODE_ATTRIBUTE } from "../../components/dashboard-shell/dashboard-theme-mode-attribute";

const DIALOG_TITLE = "キャンバスを完全に削除";
export const PERMANENT_DELETE_WARNING = "キャンバスと添付を完全に削除します。元に戻せません。";
export const PERMANENT_DELETE_INPUT_LABEL = `確認のため「${CANVAS_PERMANENT_DELETE_CONFIRMATION_TEXT}」と入力してください`;
const BLOCKED_INPUT_MESSAGE =
  "貼り付け・ドラッグ＆ドロップ・自動入力では削除できません。入力欄を空にしてから、手で入力してください。";

/**
 * 手入力として扱わない入力の種類（InputEvent.inputType）。貼り付け・ドロップ・置換（自動修正・自動入力）など。
 * 日本語入力の確定（insertText / insertCompositionText）は手入力として扱う。
 */
const NON_MANUAL_INPUT_TYPES: ReadonlySet<string> = new Set([
  "insertFromPaste",
  "insertFromPasteAsQuotation",
  "insertFromDrop",
  "insertFromYank",
  "insertReplacementText",
  "insertLink",
  "deleteByDrag",
]);

/**
 * 日本語入力（IME）の変換中の操作として届いたキー入力か。変換中の keydown は isComposing が真になり、Safari は
 * 変換を終えた後に keyCode 229 の keydown を送る（リポジトリのほかの入力欄と同じ判定）。
 */
function isImeKeyEvent(event: ReactKeyboardEvent<Element>): boolean {
  const nativeEvent = event.nativeEvent as globalThis.KeyboardEvent & {
    readonly isComposing?: boolean | undefined;
    readonly keyCode?: number | undefined;
  };
  return nativeEvent.isComposing === true || nativeEvent.keyCode === 229;
}

interface Props {
  /** 削除するキャンバスの名前（表示用）。 */
  readonly canvasTitle: string;
  /**
   * portal 先（Modal は body 直下へ描画される）でも TOP と同じ配色トークンを解決するためのテーマモード。
   * 省略時は属性を付けない（ライトの既定値で解決する）。
   */
  readonly themeMode?: ReturnType<typeof useThemeMode> | undefined;
  /** キャンセル（ボタン・Esc）。送信中は呼ばない。 */
  readonly onCancel: () => void;
  /** 確認の入力が一致した状態で削除を押したとき。完了（成否を問わない）まで送信中の表示にする。 */
  readonly onConfirm: (confirmation: string) => Promise<void>;
}

/**
 * 呼び出し側は、開くたび・対象が替わるたびに key を変えて描画し直す（入力と入力から決まる状態を残さない）。
 */
export function CanvasPermanentDeleteDialog({
  canvasTitle,
  themeMode,
  onCancel,
  onConfirm,
}: Props): JSX.Element {
  const [value, setValue] = useState("");
  const [composing, setComposing] = useState(false);
  // 手入力でない方法（自動入力など）で値が変わった。入力欄が空になるまで有効にしない。
  const [tainted, setTainted] = useState(false);
  // 貼り付け・ドロップを止めた、または手入力でない値を検出したことの案内。
  const [blockedNotice, setBlockedNotice] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const mountedRef = useRef(true);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // 開いた直後は入力欄へフォーカスを置く。あわせて、ネイティブの beforeinput / input で手入力でない入力を
  // 見分ける（React の合成イベントは inputType を持たないため）。貼り付け・ドロップは beforeinput で止め、
  // 止められない自動入力などは、値が変わったことを記録して削除ボタンを有効にしない。
  useEffect(() => {
    const input = inputRef.current;
    if (input === null) return undefined;
    input.focus();
    const handleBeforeInput = (event: Event): void => {
      const inputType = (event as InputEvent).inputType;
      if (typeof inputType === "string" && NON_MANUAL_INPUT_TYPES.has(inputType)) {
        event.preventDefault();
        setBlockedNotice(true);
      }
    };
    const handleInput = (event: Event): void => {
      const inputType = (event as Partial<InputEvent>).inputType;
      // 自動入力は inputType を持たない input イベント（または置換の inputType）で届く。
      if (typeof inputType !== "string" || inputType === "" || NON_MANUAL_INPUT_TYPES.has(inputType)) {
        setTainted(true);
        setBlockedNotice(true);
      }
    };
    input.addEventListener("beforeinput", handleBeforeInput);
    input.addEventListener("input", handleInput);
    return () => {
      input.removeEventListener("beforeinput", handleBeforeInput);
      input.removeEventListener("input", handleInput);
    };
  }, []);

  const enabled =
    !submitting && !composing && !tainted && isCanvasPermanentDeleteConfirmed(value);

  const handleChange = (next: string): void => {
    setValue(next);
    if (next.length === 0) {
      setTainted(false);
      setBlockedNotice(false);
    }
  };

  const handleConfirm = async (): Promise<void> => {
    // 押した時点の入力で改めて確かめる（連打・変換中・手入力でない値では送らない）。
    if (submittingRef.current || !enabled) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      await onConfirm(value);
    } finally {
      submittingRef.current = false;
      if (mountedRef.current) setSubmitting(false);
    }
  };

  const blockTransfer = (
    event: ReactClipboardEvent<HTMLInputElement> | ReactDragEvent<HTMLInputElement>
  ): void => {
    event.preventDefault();
    setBlockedNotice(true);
  };

  // Esc で閉じる（送信中は閉じず、結果が出るまで画面を残す）。Modal は入力欄を含む内容では Esc・外側の
  // クリックで onClose を呼ばない（入力の途中で閉じないための既定の扱い）ため、Esc だけをここで扱う。
  // 外側のクリックでは閉じない（取り消せない操作の確認のため、明示の操作でだけ閉じる）。
  // 日本語入力の変換中の Esc（変換の取り消し）では閉じない（入力を消さない）。Safari は変換を終えた後に
  // keyCode 229 の keydown を送るため、それも変換中の操作として扱う。
  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDialogElement>): void => {
    if (event.key === "Escape" && !isImeKeyEvent(event)) {
      event.preventDefault();
      if (!submittingRef.current) onCancel();
    }
    // ダッシュボードのキーボード操作（カードのメニューなど）へ伝えない。
    event.stopPropagation();
  };

  return (
    <Modal
      open={true}
      aria-label={DIALOG_TITLE}
      className="canvas-permanent-delete-dialog"
      data-testid="canvas-permanent-delete-dialog"
      onKeyDown={handleKeyDown}
      {...(themeMode !== undefined ? { [DASHBOARD_THEME_MODE_ATTRIBUTE]: themeMode } : {})}
    >
      <div className="canvas-permanent-delete-dialog__body">
        <h2 className="canvas-permanent-delete-dialog__title">{DIALOG_TITLE}</h2>
        <p className="canvas-permanent-delete-dialog__name" data-testid="permanent-delete-canvas-title">
          {canvasTitle}
        </p>
        <p className="canvas-permanent-delete-dialog__warning">{PERMANENT_DELETE_WARNING}</p>
        <OutlinedInputText
          label={PERMANENT_DELETE_INPUT_LABEL}
          inputProps={{
            ref: inputRef,
            value,
            onChange: handleChange,
            placeholder: CANVAS_PERMANENT_DELETE_CONFIRMATION_TEXT,
            autoFocus: true,
            autoComplete: "off",
            autoCorrect: "off",
            autoCapitalize: "off",
            spellCheck: false,
            name: "canvas-permanent-delete-confirmation",
            "data-testid": "permanent-delete-confirmation-input",
            onPaste: blockTransfer,
            onDrop: blockTransfer,
            onKeyDown: (event: ReactKeyboardEvent<HTMLInputElement>) => {
              // 入力欄の Enter は何も送らない（変換確定の Enter を削除の送信に使わない）。変換確定の Enter は
              // IME の既定動作に委ねる（フォームを置かないため、既定動作でも何も送られない）。
              if (event.key !== "Enter" || isImeKeyEvent(event)) return;
              event.preventDefault();
            },
            onCompositionStart: () => {
              setComposing(true);
            },
            onCompositionEnd: () => {
              setComposing(false);
            },
          }}
        />
        {blockedNotice ? (
          <p className="canvas-permanent-delete-dialog__hint" aria-hidden="true">
            {BLOCKED_INPUT_MESSAGE}
          </p>
        ) : null}
        {/* 読み上げの領域は最初から置き、文言だけを替える（領域ごと差し込むと読み上げられないことがある）。
            見える案内は上の段落で、読み上げはこの領域から 1 回だけ行う。 */}
        <p className="visually-hidden" role="status">
          {blockedNotice ? BLOCKED_INPUT_MESSAGE : ""}
        </p>
        <div className="canvas-permanent-delete-dialog__actions">
          <Button type="button" variant="action" disabled={submitting} onClick={onCancel}>
            キャンセル
          </Button>
          <Button
            type="button"
            variant="action danger emphasis"
            disabled={!enabled}
            busy={submitting}
            onClick={() => void handleConfirm()}
            data-testid="permanent-delete-submit"
          >
            削除
          </Button>
        </div>
      </div>
    </Modal>
  );
}
